import { defineStore } from 'pinia'
import { checkDocument, setLocalStorage, getLocalStorage } from '@js/helpers.ts'

const COOKIE_AUTO_UNREAD = 'jivo_auto_unread_v1'      // отметка, что мы показывали/планируем автонепрочитанное
const COOKIE_HAD_CONVO  = 'jivo_had_conversation_v1'  // отметка, что с этим браузером уже была переписка
const AUTO_DELAY_MS = 10000                           // 10 секунд
const YEAR_IN_MINUTES = 525600                        // TTL для кук (1 год)

function deleteCookie(name) {
    // setLocalStorage обычно принимает минуты, отрицательные — удалить
    try { setLocalStorage(name, '', -1) } catch {}
}

export const useJivoStore = defineStore('jivoStore', {
    state: () => ({
        hasUnreadMessages: false,
        unreadCount: 0,
        chatMode: null,        // 'online' | 'offline' | null
        isLoaded: false,

        // внутренние флаги
        _callbacksInstalled: false,
        _listenersBound: false,

        // ссылки на обработчики для корректного removeEventListener
        __handleLoaded: null,
        __handleIncoming: null,
        __handleOpen: null,

        // таймер автонепрочитанного
        __autoUnreadTimer: null,
    }),

    actions: {
        init() {
            if (!checkDocument()) return
            this.installCallbacks()
            this.bindWindowEvents()
            this.syncFromApi() // если API уже готов — сразу подтянем статус/непрочитанные
        },

        teardown() {
            if (!checkDocument()) return
            this.clearAutoTimer()
            this.unbindWindowEvents()
        },

        // --- Публичные методы управления чатом ---
        openJivoChat() {
            this.hasUnreadMessages = false
            this.unreadCount = 0
            if (!checkDocument()) return

            if (window.jivo_api && typeof window.jivo_api.open === 'function') {
                window.jivo_api.open({ start: 'chat' })

                // Сразу ставим "переписка была" и убираем авто-куку
                this.recordConversationEvent()
            } else {
                console.warn('Jivo API не инициализирован')
            }
        },

        markAllRead() {
            this.unreadCount = 0
            this.hasUnreadMessages = false
        },

        // --- Внутренняя установка глобальных колбэков Jivo ---
        installCallbacks() {
            if (!checkDocument() || this._callbacksInstalled || window.__jivoCallbacksInstalled) return

            window.jivo_onLoadCallback = () => {
                try {
                    const mode = window.jivo_api?.chatMode?.()
                    const unread = window.jivo_api?.getUnreadMessagesCount?.() ?? 0
                    window.dispatchEvent(new CustomEvent('jivo:loaded', { detail: { mode, unread } }))
                } catch {
                    window.dispatchEvent(new CustomEvent('jivo:loaded', { detail: {} }))
                }
            }

            // входящее сообщение (оператор/бот)
            window.jivo_onMessageReceived = () => {
                window.dispatchEvent(new CustomEvent('jivo:incoming'))
            }

            // окно открыто
            window.jivo_onOpen = () => {
                window.dispatchEvent(new CustomEvent('jivo:open'))
            }

            // клиент отправил сообщение (поможет понять, что переписка началась)
            window.jivo_onMessageSent = () => {
                window.dispatchEvent(new CustomEvent('jivo:client-first'))
            }

            window.__jivoCallbacksInstalled = true
            this._callbacksInstalled = true
        },

        bindWindowEvents() {
            if (!checkDocument() || this._listenersBound) return

            this.__handleLoaded = (e) => this.handleLoaded(e)
            this.__handleIncoming = () => this.handleIncoming()
            this.__handleOpen = () => this.handleOpen()

            window.addEventListener('jivo:loaded', this.__handleLoaded)
            window.addEventListener('jivo:incoming', this.__handleIncoming)
            window.addEventListener('jivo:open', this.__handleOpen)

            // отметим переписку при первом исходящем клиента
            window.addEventListener('jivo:client-first', this.recordConversationEvent)

            this._listenersBound = true
        },

        unbindWindowEvents() {
            if (!checkDocument() || !this._listenersBound) return

            window.removeEventListener('jivo:loaded', this.__handleLoaded)
            window.removeEventListener('jivo:incoming', this.__handleIncoming)
            window.removeEventListener('jivo:open', this.__handleOpen)
            window.removeEventListener('jivo:client-first', this.recordConversationEvent)

            this.__handleLoaded = null
            this.__handleIncoming = null
            this.__handleOpen = null
            this._listenersBound = false
        },

        // --- Хэндлеры событий ---
        handleLoaded(e) {
            const mode = e?.detail?.mode ?? null
            const unread = Number(e?.detail?.unread ?? 0)
            if (mode) this.chatMode = mode
            this.unreadCount = unread
            this.hasUnreadMessages = unread > 0
            this.isLoaded = true

            // решаем, запускать ли автонепрочитанное
            this.maybeScheduleAutoUnread()
        },

        handleIncoming() {
            // любое входящее = переписка была
            this.recordConversationEvent()

            this.unreadCount += 1
            this.hasUnreadMessages = this.unreadCount > 0
        },

        handleOpen() {
            // открытие окна — считаем, что переписка началась/ведётся
            this.recordConversationEvent()
            this.markAllRead()
        },

        // --- Разовая синхронизация при init(), если API уже готов ---
        syncFromApi() {
            try {
                if (!window.jivo_api) return
                const mode = window.jivo_api?.chatMode?.()
                const unread = Number(window.jivo_api?.getUnreadMessagesCount?.() ?? 0)
                if (mode) this.chatMode = mode
                this.unreadCount = unread
                this.hasUnreadMessages = unread > 0
            } catch {}
            // даже если onLoad ещё не пришёл — проверим условие для автонепрочитанного
            this.maybeScheduleAutoUnread()
        },

        // --- Автосценарий непрочитанного для первого визита ---
        maybeScheduleAutoUnread() {
            if (!checkDocument()) return

            const hadConvo = !!getLocalStorage(COOKIE_HAD_CONVO)
            if (hadConvo) {
                // переписка была — никакого автонепрочитанного, чистим отметку, если есть
                deleteCookie(COOKIE_AUTO_UNREAD)
                this.clearAutoTimer()
                return
            }

            // переписки не было; если уже есть реальное непрочитанное — не нужно фейковое
            if (this.unreadCount > 0 || this.hasUnreadMessages) {
                // на всякий случай отметим, что автосценарий не нужен
                deleteCookie(COOKIE_AUTO_UNREAD)
                this.clearAutoTimer()
                return
            }

            // ставим куку-отметку и планируем автонепрочитанное через 10 секунд
            if (!getLocalStorage(COOKIE_AUTO_UNREAD)) {
                setLocalStorage(COOKIE_AUTO_UNREAD, '1', YEAR_IN_MINUTES)
            }

            if (!this.__autoUnreadTimer) {
                this.__autoUnreadTimer = setTimeout(() => {
                    // финальная проверка перед применением
                    const hadConvoNow = !!getLocalStorage(COOKIE_HAD_CONVO)
                    if (!hadConvoNow && this.unreadCount === 0) {
                        this.unreadCount = 1
                        this.hasUnreadMessages = true
                    }
                    this.__autoUnreadTimer = null
                }, AUTO_DELAY_MS)
            }
        },

        recordConversationEvent() {
            // ставим куку "переписка была", отменяем автосценарий и удаляем его куку
            if (!getLocalStorage(COOKIE_HAD_CONVO)) {
                setLocalStorage(COOKIE_HAD_CONVO, '1', YEAR_IN_MINUTES)
            }
            deleteCookie(COOKIE_AUTO_UNREAD)
            this.clearAutoTimer()
        },

        clearAutoTimer() {
            if (this.__autoUnreadTimer) {
                clearTimeout(this.__autoUnreadTimer)
                this.__autoUnreadTimer = null
            }
        },
    },
})
